/**
 * src/services/liveSessionService.ts
 *
 * Client bindings for the backend paid live-session routes
 * (`/api/live-sessions/*`).
 *
 * Flow from the broadcaster UI's perspective:
 *
 *   1. Call `requestLiveSession({ wallet, country })`.
 *   2. If the returned `requiresPayment === false`, jump straight to
 *      `connectWebRtc({ broadcasterId, liveSessionId, grantToken })`.
 *   3. Otherwise, execute the payment through the existing wallet flow
 *      (see `src/v2/services/vaultService::executePayment`) and submit
 *      the resulting Stellar tx hash / reference to
 *      `confirmLivePayment(sessionId, { paymentReference, paidAmount })`.
 *      The backend records the evidence and returns a grant token.
 *   4. On payment failure, call `markLivePaymentFailed(sessionId, reason)`
 *      so the backend can close the session state cleanly.
 *   5. After the broadcast, call `stopLiveSession(sessionId)` (the
 *      backend will do the same when the signaling socket closes, so
 *      this is defence-in-depth rather than a hard requirement).
 *   6. Poll `getLiveSession(sessionId)` to render the countdown and
 *      ended-reason UI — the backend is authoritative on when a session
 *      expires.
 */

const DEFAULT_BACKEND = (
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_BACKEND_URL) || ''
) as string;

export type LiveSessionStatus =
  | 'requested'
  | 'payment_required'
  | 'paid_ready'
  | 'live'
  | 'ended_user'
  | 'ended_duration'
  | 'ended_admin'
  | 'ended_policy'
  | 'ended_payment_failed'
  | 'ended_error'
  | 'blocked_by_policy';

export interface LivePolicySnapshot {
  policy: number;
  sessionCost: string;
  maxDurationSeconds: number;
  effectiveMaxDurationSeconds: number;
  usingFallbackDuration: boolean;
  policyVersion: number;

  // ── V2 fields (optional so older backends still deserialise) ──
  extensionsEnabled?: boolean;
  extensionCost?: string;
  extensionDurationSeconds?: number;
  effectiveExtensionCost?: string;
  effectiveExtensionSeconds?: number;
  maxTotalDurationSeconds?: number;
  effectiveMaxTotalDurationSeconds?: number;
  maxExtensions?: number;
  effectiveMaxExtensions?: number;
  minExtensionSeconds?: number;
  effectiveMinExtensionSeconds?: number;
  platformBps?: number;
  broadcasterBps?: number;
}

export interface ApproxLocation {
  /** Latitude in decimal degrees (WGS-84).  Positive = north. */
  lat: number;
  /** Longitude in decimal degrees (WGS-84).  Positive = east. */
  lng: number;
}

export interface SessionExtension {
  at: string;
  paymentReference: string;
  paidAmount: string;
  payerWallet: string | null;
  fromViewer: boolean;
  previousScheduledEndAt: string;
  newScheduledEndAt: string;
  extensionSeconds: number;
  /** True when the policy-driven cap forced a shorter-than-requested extension. */
  clamped?: boolean;
  /** What the policy quoted at the time of extension (for audit parity). */
  extensionCostQuoted?: string;
}

/**
 * Read-only preview returned by `GET /api/live-sessions/:id/extension-quote`.
 * Used by the HUD to show the viewer/broadcaster the exact cost and seconds
 * they'd gain by extending _right now_, or the precise policy reason the
 * server will reject with (e.g. `max_extensions_reached`) so the UI can
 * disable the button and render a friendly explanation without guessing.
 */
export interface ExtensionQuote {
  ok: boolean;
  /** Populated only when `ok === true`. */
  extensionSeconds?: number;
  extensionCost?: string;
  clamped?: boolean;
  newScheduledEndMs?: number;
  policy?: {
    extensionsEnabled?: boolean;
    effectiveExtensionCost?: string;
    effectiveExtensionSeconds?: number;
    effectiveMaxExtensions?: number;
    effectiveMaxTotalDurationSeconds?: number;
    effectiveMinExtensionSeconds?: number;
  };
  /** Populated only when `ok === false`. */
  reason?: string;
  details?: Record<string, unknown>;
}

export interface SettlementReceipt {
  sessionId: string;
  kind: 'start' | 'extension';
  incomingReference: string;
  platformBps: number;
  broadcasterBps: number;
  paidStroops: string;
  platformStroops: string;
  broadcasterStroops: string;
  status: 'succeeded' | 'failed' | 'skipped';
  reason: string | null;
  broadcasterTxHash: string | null;
  settledAt: string | null;
}

export interface LiveSession {
  id: string;
  broadcasterId: string;
  wallet: string | null;
  country: number | null;
  status: LiveSessionStatus;
  policySnapshot: LivePolicySnapshot;
  sessionCost: string;
  effectiveMaxDurationSeconds: number;
  requiresPayment: boolean;
  createdAt: string;
  paidAt: string | null;
  paymentReference: string | null;
  paidAmount: string | null;
  startedAt: string | null;
  scheduledEndAt: string | null;
  endedAt: string | null;
  endedReason: string | null;
  grantExpiresAt: string;
  /** Live-presence scope: 'public' (anyone) or 'groups' (trust-group members). */
  visibility?: 'public' | 'groups';
  /** The on-chain geo-session id this broadcast is bound to, if any. */
  onchainSessionId?: number | null;
  /** Quantised (~11 km) broadcaster location; `null` when not shared. */
  approxLat: number | null;
  approxLng: number | null;
  /** Append-only ledger of pay-again-to-extend events. */
  extensions: SessionExtension[];
  /** Append-only ledger of V2 revenue-split settlement receipts
   *  emitted by `liveSessionSettlementService` for each inbound
   *  payment (start + every extension).  Always present on V2
   *  backends; pre-V2 servers omit the field entirely. */
  settlements?: SettlementReceipt[];
}

export interface RequestLiveSessionResult {
  session: LiveSession;
  grantToken: string;
  requiresPayment: boolean;
  sessionCost: string;
  effectiveMaxDurationSeconds: number;
}

export interface RequestLiveSessionFailure {
  reason: string;
  policy?: any;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${DEFAULT_BACKEND}${path}`, {
    ...(init || {}),
    headers: { 'Content-Type': 'application/json', ...(init?.headers as any) },
  });
  const data = await resp.json().catch(() => ({} as any));
  if (!resp.ok || data.ok === false) {
    const err: any = new Error(data.reason || data.error || `request_failed_${resp.status}`);
    err.payload = data;
    throw err;
  }
  return data as T;
}

export async function requestLiveSession(input: {
  wallet?: string; country?: number; broadcasterId?: string;
  /** Optional approximate location; server rounds to 0.1° for privacy. */
  approxLocation?: ApproxLocation;
  /** Live-presence scope. Omit or 'public' for an open broadcast. */
  visibility?: 'public' | 'groups';
  /** On-chain geo-session id (from start_session) that carries the scope. */
  onchainSessionId?: number | null;
  /** Direct trust-group scope (used when no on-chain session id is passed). */
  trustGroupIds?: number[];
}): Promise<RequestLiveSessionResult> {
  const data = await request<any>(`/api/live-sessions/request`, {
    method: 'POST', body: JSON.stringify(input),
  });
  return {
    session: data.session,
    grantToken: data.grantToken,
    requiresPayment: Boolean(data.requiresPayment),
    sessionCost: String(data.sessionCost || '0'),
    effectiveMaxDurationSeconds: Number(data.effectiveMaxDurationSeconds || 0),
  };
}

export async function confirmLivePayment(
  sessionId: string,
  details: { paymentReference: string; paidAmount?: string },
): Promise<{ session: LiveSession; grantToken: string }> {
  const data = await request<any>(`/api/live-sessions/${sessionId}/confirm-payment`, {
    method: 'POST', body: JSON.stringify(details),
  });
  return { session: data.session, grantToken: data.grantToken };
}

export async function markLivePaymentFailed(sessionId: string, reason?: string): Promise<LiveSession> {
  const data = await request<any>(`/api/live-sessions/${sessionId}/mark-failed`, {
    method: 'POST', body: JSON.stringify({ reason }),
  });
  return data.session;
}

export async function stopLiveSession(sessionId: string): Promise<LiveSession> {
  const data = await request<any>(`/api/live-sessions/${sessionId}/stop`, {
    method: 'POST', body: '{}',
  });
  return data.session;
}

export async function getLiveSession(sessionId: string): Promise<LiveSession> {
  const data = await request<any>(`/api/live-sessions/${sessionId}`);
  return data.session;
}

/**
 * Record a pay-again-to-extend payment.  The frontend must have
 * executed the Stellar transfer through the same treasury first; this
 * call only records the receipt and asks the server to re-arm its
 * authoritative duration timer.  All peers currently connected to the
 * signaling hub receive a `session_extended` push in response.
 */
export async function extendLiveSession(
  sessionId: string,
  details: {
    paymentReference: string;
    paidAmount?: string;
    payerWallet?: string;
    fromViewer?: boolean;
  },
): Promise<{ session: LiveSession; extensionSeconds?: number; clamped?: boolean }> {
  const data = await request<any>(`/api/live-sessions/${sessionId}/extend`, {
    method: 'POST',
    body: JSON.stringify(details),
  });
  return {
    session: data.session,
    extensionSeconds: typeof data.extensionSeconds === 'number' ? data.extensionSeconds : undefined,
    clamped: typeof data.clamped === 'boolean' ? data.clamped : undefined,
  };
}

/**
 * Pre-extension "what-if" — returns the exact seconds and cost the
 * backend will apply if the caller extends right now, or a structured
 * rejection reason (one of:
 *   `extensions_disabled` / `viewer_extensions_disabled`
 *   `max_extensions_reached`
 *   `max_total_duration_reached`
 *   `below_min_extension_seconds`).
 * Non-mutating and safe to poll — the backend does not charge for
 * quoting.  Callers should re-quote after each successful extension
 * because the clamp depends on elapsed session time.
 */
export async function quoteLiveSessionExtension(
  sessionId: string,
  opts: { fromViewer?: boolean } = {},
): Promise<ExtensionQuote> {
  const qs = opts.fromViewer ? `?fromViewer=1` : '';
  try {
    const data = await request<any>(`/api/live-sessions/${sessionId}/extension-quote${qs}`);
    return {
      ok: data.ok !== false,
      extensionSeconds:   typeof data.extensionSeconds === 'number' ? data.extensionSeconds : undefined,
      extensionCost:      typeof data.extensionCost === 'string' ? data.extensionCost : undefined,
      clamped:            typeof data.clamped === 'boolean' ? data.clamped : undefined,
      newScheduledEndMs:  typeof data.newScheduledEndMs === 'number' ? data.newScheduledEndMs : undefined,
      policy:             data.policy,
      reason:             data.reason,
      details:            data.details,
    };
  } catch (err: any) {
    // The backend returns 4xx for policy-driven rejections, but we still want the payload.
    const payload = err?.payload;
    if (payload) {
      return {
        ok: false,
        reason:  payload.reason || err.message,
        details: payload.details,
        policy:  payload.policy,
      };
    }
    return { ok: false, reason: err?.message || 'quote_failed' };
  }
}

/**
 * List sessions currently streaming, optionally filtered by wallet.
 * Used by ProfileOverlay to detect whether a peer is broadcasting
 * without subscribing to the signaling hub.
 */
export async function listActiveBroadcasts(wallet?: string, viewer?: string): Promise<LiveSession[]> {
  const params = new URLSearchParams();
  if (wallet) params.set('wallet', wallet);
  // `viewer` is the requesting wallet's identity — required to discover
  // trust-group-restricted broadcasts you're a member of.
  if (viewer) params.set('viewer', viewer);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const data = await request<{ sessions: LiveSession[] }>(`/api/live-sessions/active${qs}`);
  return Array.isArray(data.sessions) ? data.sessions : [];
}

// ── Viewer scope grant (trust-group-restricted broadcasts) ─────────────────

export interface ViewerChallenge {
  challengeId: string;
  transactionXdr: string;
  networkPassphrase: string;
  expiresAt?: string;
  [k: string]: any;
}

/**
 * Fetch a SEP-10 style challenge to prove ownership of `wallet` before
 * requesting a viewer token for a restricted broadcast.
 */
export async function getViewerChallenge(wallet: string, network?: string): Promise<ViewerChallenge> {
  const params = new URLSearchParams({ wallet });
  if (network) params.set('network', network);
  const data = await request<any>(`/api/live-sessions/viewer-challenge?${params.toString()}`);
  return data as ViewerChallenge;
}

/**
 * Exchange a signed challenge for a viewer token. Returns `{ token }`
 * for restricted rooms, or `{ token: null, public: true }` for public
 * broadcasts (no token needed).
 */
export async function requestViewerGrant(
  sessionId: string,
  input: { wallet: string; challengeId: string; signedXdr: string },
): Promise<{ token: string | null; public?: boolean }> {
  const data = await request<any>(`/api/live-sessions/${encodeURIComponent(sessionId)}/viewer-grant`, {
    method: 'POST', body: JSON.stringify(input),
  });
  return { token: data.token ?? null, public: !!data.public };
}

/**
 * End-to-end viewer token acquisition for a restricted broadcast:
 *   1. fetch a SEP-10 challenge for `wallet`,
 *   2. sign it with the connected wallet,
 *   3. exchange it for a short-lived viewer token.
 *
 * Returns the token (or `null` for public rooms). Throws on refusal —
 * callers should surface a "restricted to trust group" state. Only
 * G-address wallets can sign the challenge; C (passkey) viewers of a
 * restricted room are not yet supported and will throw `invalid_wallet`.
 */
export async function obtainViewerToken(
  sessionId: string,
  wallet: string,
  signer: { signTransaction: (xdr: string, network: string) => Promise<string> },
): Promise<string | null> {
  const challenge = await getViewerChallenge(wallet);
  const signedXdr = await signer.signTransaction(challenge.transactionXdr, challenge.networkPassphrase);
  const grant = await requestViewerGrant(sessionId, {
    wallet,
    challengeId: challenge.challengeId,
    signedXdr,
  });
  return grant.token;
}

// ── Operator-driven AI agent TTS while live ───────────────────────────────

export interface AgentLiveSpeakResult {
  voice:        string;
  format:       string;
  mime:         string;
  duration_ms:  number;
  characters:   number;
  text_sha256:  string;
  audio_base64: string;
  cached:       boolean;
  broadcast:    {
    sent:          boolean;
    reason?:       string;
    session_id?:   string;
    viewer_count?: number;
    message?:      string;
  } | null;
}

/**
 * Make the operator's AI agent "say" something during a live broadcast.
 *
 * Uses the live-session grantToken (already issued to this operator for
 * this broadcaster) to authorise TTS-on-behalf-of-agent so the operator
 * doesn't need the agent's Ed25519 secret in the browser. The backend
 * synthesises via Azure Speech and fans the rendered caption + audio
 * into the agent's WebRTC room as a `live_agent_caption` event — every
 * connected viewer (and the operator's own broadcast preview) hears the
 * audio mixed into the broadcast and sees the caption text.
 */
export async function speakAsAgentDuringLive(
  sessionId: string,
  details: { grantToken: string; text: string; voice?: string },
): Promise<AgentLiveSpeakResult> {
  return await request<AgentLiveSpeakResult>(
    `/api/live-sessions/${encodeURIComponent(sessionId)}/agent-speak`,
    {
      method: 'POST',
      body: JSON.stringify({
        grantToken: details.grantToken,
        text:       details.text,
        voice:      details.voice,
      }),
    },
  );
}
